"use client";

import { DatabaseBackup, LoaderCircle } from "lucide-react";
import { useState } from "react";
import {
  cloudflareAccessReauthenticationPath,
  needsCloudflareAccessReauthentication,
} from "../../lib/auth-navigation";
import { LoadingActivityPanel } from "./ui/LoadingActivityPanel";

const LOGIN_TIMEOUT_MS = 20_000;

async function fetchWithTimeout(input: RequestInfo | URL, init: RequestInit, timeoutMs: number) {
  const controller = new AbortController();
  const timeout = window.setTimeout(() => controller.abort(), timeoutMs);
  try {
    return await fetch(input, { credentials: "include", ...init, signal: controller.signal });
  } finally {
    window.clearTimeout(timeout);
  }
}

async function responsePayload(response: Response) {
  const text = await response.text();
  if (!text) return {} as { code?: string; error?: string; requiresSetup?: boolean; ok?: boolean };
  try {
    return JSON.parse(text) as { code?: string; error?: string; requiresSetup?: boolean; ok?: boolean };
  } catch {
    const contentType = response.headers.get("content-type")?.toLowerCase() ?? "";
    const plainMessage = text.trim();
    return {
      error: contentType.includes("text/plain") && plainMessage && plainMessage.length <= 240
        ? plainMessage
        : response.ok ? "服务器返回了无法识别的结果" : `请求失败（HTTP ${response.status}）`,
    };
  }
}

function requestErrorMessage(error: unknown, fallback: string) {
  if (error instanceof Error && error.name === "AbortError") return "请求超时，请检查网络后重试；页面不会再一直停留在处理中";
  if (error instanceof TypeError && /failed to fetch|networkerror|load failed/i.test(error.message)) {
    return "无法连接家庭资产 API；请截图当前处理信息，并检查 Cloudflare Access/CORS 或网络状态";
  }
  return error instanceof Error ? error.message : fallback;
}

export function HouseholdUnlock({
  email,
  requiresSetup = false,
  allowMigrationReset = false,
  logoutHref = "/api/auth/logout",
}: {
  email: string;
  requiresSetup?: boolean;
  allowMigrationReset?: boolean;
  logoutHref?: string;
}) {
  const [password, setPassword] = useState("");
  const [confirmation, setConfirmation] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [resetLoading, setResetLoading] = useState(false);

  async function submit(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (requiresSetup && password !== confirmation) {
      setError("两次输入的家庭密码不一致");
      return;
    }
    setLoading(true);
    setError("");
    try {
      const response = await fetchWithTimeout("/api/household/session", {
        method: requiresSetup ? "PATCH" : "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(requiresSetup
          ? { initialize: true, newPassword: password, confirmPassword: confirmation }
          : { password }),
      }, LOGIN_TIMEOUT_MS);
      const payload = await responsePayload(response);
      if (!response.ok) {
        if (needsCloudflareAccessReauthentication(payload.code)) {
          const returnTo = `${window.location.pathname}${window.location.search}${window.location.hash}`;
          window.location.replace(cloudflareAccessReauthenticationPath(returnTo));
          return;
        }
        if (payload.requiresSetup) window.location.reload();
        throw new Error(payload.error ?? "无法进入家庭空间");
      }
      window.location.replace("/");
    } catch (requestError) {
      setError(requestErrorMessage(requestError, "无法进入家庭空间"));
    } finally {
      setLoading(false);
    }
  }

  async function prepareSitesMigration() {
    setResetLoading(true);
    setError("");
    try {
      const response = await fetchWithTimeout("/api/household/session", { method: "PUT" }, LOGIN_TIMEOUT_MS);
      const payload = await responsePayload(response);
      if (!response.ok || !payload.ok) throw new Error(payload.error ?? "无法准备首次数据迁移");
      window.location.reload();
    } catch (requestError) {
      setError(requestErrorMessage(requestError, "无法准备首次数据迁移"));
      setResetLoading(false);
    }
  }

  return (
    <main className="unlock-shell">
      <div className="unlock-orbit orbit-a" aria-hidden="true" />
      <div className="unlock-orbit orbit-b" aria-hidden="true" />
      <section className="unlock-card" aria-labelledby="unlock-title">
        <div className="unlock-brand">
          <span className="brand-mark" aria-hidden="true" />
          <div><strong>权宇宙</strong><small>家庭资产星图</small></div>
        </div>
        <div className="unlock-copy">
          <span className="eyebrow">Private family space</span>
          <h1 id="unlock-title">{requiresSetup ? "创建家庭访问密码" : "欢迎回到家庭资产轨道"}</h1>
          <p>{requiresSetup
            ? "当前家庭尚未设置访问密码。创建后，资产数据只对获授权 Google 邮箱和持有密码的家庭成员开放。"
            : "Google 邮箱身份已确认，还需要家庭共同密码才能读取资产数据。"}</p>
        </div>
        {allowMigrationReset ? <section className="setup-migration-panel" aria-labelledby="migration-reset-title">
          <div className="setup-migration-heading"><DatabaseBackup size={20} /><div><strong id="migration-reset-title">准备首次数据迁移</strong><small>仅清除旧家庭密码凭据，不删除资产、账单或投资数据</small></div></div>
          <button className="setup-migration-submit" type="button" onClick={prepareSitesMigration} disabled={resetLoading || loading}>
            {resetLoading ? <LoaderCircle className="spin" size={16} /> : <DatabaseBackup size={16} />}{resetLoading ? "正在准备…" : "清除旧密码并开始迁移"}
          </button>
        </section> : null}
        <form onSubmit={submit} className="unlock-form" aria-busy={loading || resetLoading}>
          <label htmlFor="household-password">{requiresSetup ? "新家庭密码" : "家庭密码"}</label>
          <div className="password-row">
            <input
              id="household-password"
              type="password"
              autoComplete={requiresSetup ? "new-password" : "current-password"}
              value={password}
              onChange={(event) => setPassword(event.target.value)}
              placeholder={requiresSetup ? "至少 10 个字符" : "输入共同家庭密码"}
              required
              autoFocus
              disabled={loading || resetLoading}
            />
            <button type="submit" disabled={loading || resetLoading}>{loading ? <><LoaderCircle className="spin" size={16} />正在验证…</> : requiresSetup ? "创建并进入" : "进入星图"}</button>
          </div>
          {requiresSetup ? <><label htmlFor="household-password-confirmation">确认新密码</label><div className="password-row password-row-single"><input
            id="household-password-confirmation"
            type="password"
            autoComplete="new-password"
            value={confirmation}
            onChange={(event) => setConfirmation(event.target.value)}
            placeholder="再次输入新密码"
            required
            disabled={loading || resetLoading}
          /></div><small className="unlock-password-policy">不要使用邮箱、连续数字或常见密码；密码不会在页面或备份中显示。</small></> : null}
          {error ? <p className="form-error" role="alert">{error}</p> : null}
        </form>
        <footer className="unlock-footer">
          <span>当前登录：{email} · <a className="identity-signout" href={logoutHref}>更换账号</a></span>
          <span>双重访问保护 · 12小时后自动锁定</span>
        </footer>
      </section>
      {loading || resetLoading ? <LoadingActivityPanel
        operation={resetLoading ? "准备首次数据迁移" : requiresSetup ? "创建家庭访问密码" : "验证家庭访问密码"}
        waitingFor="身份验证与密码处理完成"
      /> : null}
    </main>
  );
}
